import React, { useMemo, useState, useEffect } from "react";
import "./App.css";
import AccueilContent from "./AccueilContent";
import NosVehiculesContent from "./NosVehiculesContent";
import RepriseContent from "./RepriseContent";
import RecherchesPersonnaliseesContent from "./RecherchesPersonnaliseesContent";
import NosVehiculesVendusContent from "./Vendu";
import PrivacyPolicy from "./PolitiqueConf";
import VehicleDetailsPage from "./VehicleDetailsPage";
import NotFoundPage from "./NotFoundPage";
import { Helmet } from "react-helmet-async";
import { getApiBaseUrl } from "./config/api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// import { Analytics } from "@vercel/analytics/react";
// import { SpeedInsights } from '@vercel/speed-insights/react';

const TRACK_API = getApiBaseUrl();

function normalizePath(pathname) {
  if (!pathname) {
    return "/";
  }

  return pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
}

function extractVehicleReference(pathname) {
  const match = normalizePath(pathname).match(/^\/vehicules\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function App() {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname));
  const scrollYRef = React.useRef(0);

  // Désactiver la restauration de scroll native du navigateur
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Tracking : enregistrer chaque visite de page (fire-and-forget)
  useEffect(() => {
    const vehicleRef = extractVehicleReference(pathname);
    // Page view
    fetch(`${TRACK_API}/api/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: pathname }),
    }).catch(() => {});
    // Vehicle view
    if (vehicleRef) {
      fetch(`${TRACK_API}/api/track/vehicle/${encodeURIComponent(vehicleRef)}`, {
        method: 'POST',
      }).catch(() => {});
    }
  }, [pathname]);

  const toggleMenu = () => setMenuOpen((open) => !open);

  const navigateTo = (to) => {
    const targetPath = normalizePath(to);

    if (targetPath !== pathname) {
      // Sauvegarder la position de scroll dans un ref (pas dans history.state)
      scrollYRef.current = window.scrollY;
      window.history.pushState({ scrollY: scrollYRef.current }, "", targetPath);
      setPathname(targetPath);
    }

    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToVehicle = (reference) => {
    if (!reference) {
      return;
    }

    navigateTo(`/vehicules/${encodeURIComponent(reference)}`);
  };

  useEffect(() => {
    const handlePopState = () => {
      const prevPath = pathname;
      const newPath = normalizePath(window.location.pathname);
      setPathname(newPath);
      setMenuOpen(false);

      // Si on revient depuis une page véhicule vers la liste, restaurer le scroll
      const wasOnVehicle = extractVehicleReference(prevPath) !== null;
      if (wasOnVehicle && scrollYRef.current > 0) {
        const savedY = scrollYRef.current;
        let attempts = 0;
        const tryRestore = () => {
          if (document.documentElement.scrollHeight >= savedY + window.innerHeight * 0.5 || attempts >= 50) {
            window.scrollTo(0, savedY);
            return;
          }
          attempts++;
          setTimeout(tryRestore, 100);
        };
        tryRestore();
      } else {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [pathname]);

  if (isMenuOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "unset";
  }

  const routeContent = useMemo(() => {
    const vehicleReference = extractVehicleReference(pathname);

    if (vehicleReference) {
      return <VehicleDetailsPage reference={vehicleReference} navigateTo={navigateTo} isDetailPage />;
    }

    switch (pathname) {
      case "/":
        return <AccueilContent navigateTo={navigateTo} navigateToVehicle={navigateToVehicle} />;
      case "/nos-vehicules":
        return <NosVehiculesContent navigateToVehicle={navigateToVehicle} standalone />;
      case "/reprise":
        return <RepriseContent />;
      case "/recherche-personnalisee":
        return <RecherchesPersonnaliseesContent />;
      case "/vehicules-vendus":
        return <NosVehiculesVendusContent navigateToVehicle={navigateToVehicle} />;
      case "/politique-confidentialite":
        return <PrivacyPolicy />;
      default:
        return <NotFoundPage navigateTo={navigateTo} />;
    }
  }, [pathname]);

  const isDetailPage = Boolean(extractVehicleReference(pathname));

  return (
    <div className={isDetailPage ? "App detail-page" : "App"}>
      <header className="Header">
        <div className="Menu" onClick={toggleMenu}>
          {/* Remplacez 'hamburger.svg' par le chemin correct de votre fichier hamburger */}
          <img src={process.env.PUBLIC_URL + "/hamburger.svg"} alt="Menu" />
        </div>
        <div className="Logo">
          <a
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigateTo("/");
            }}
          >
            <img src={process.env.PUBLIC_URL + "/logo2.webp"} alt="Logo FLIP MY CAR" />
          </a>
        </div>
        <div className="Contact">
          {/* Remplacez 'instagram.svg' par le chemin correct de votre logo Instagram */}
          <a href="https://www.instagram.com/flipmycar.fr" target="_blank">
            <img
              src={process.env.PUBLIC_URL + "/instagram.svg"}
              alt="Instagram"
            />
          </a>
        </div>
      </header>

      <div
        className={`MenuOverlay ${isMenuOpen ? "open" : ""}`}
        onClick={toggleMenu}
      >
        <nav className="MobileMenu" onClick={(event) => event.stopPropagation()}>
          <ul>
            <li>
              <a
                href="/"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("/");
                }}
              >
                Accueil
              </a>
            </li>
            <li>
              <a
                href="/nos-vehicules"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("/nos-vehicules");
                }}
              >
                Véhicules disponibles
              </a>
            </li>
            <li>
              <a
                href="/reprise"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("/reprise");
                }}
              >
                Reprise
              </a>
            </li>
            <li>
              <a
                href="/recherche-personnalisee"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("/recherche-personnalisee");
                }}
              >
                Recherche personnalisée
              </a>
            </li>
          </ul>
        </nav>
      </div>
      <main className="Main">
        {routeContent}
      </main>
      {/* <Analytics></Analytics> */}
      {/* <SpeedInsights /> */}
      <footer className="Footer">
        <div className="ColFooter">
          <div>
           <h3>Adresse</h3>
            <p>11 rue de Joyenval</p>
            <p>Chambourcy 78240</p>
          </div>
          <div>
            <h3>Téléphone</h3>
            <p>06 82 75 46 29</p>
            <p>Également par SMS</p>
          </div>
          <div>
            <h3>Horaires</h3>
            <p>Du lundi au samedi</p>
            <p>De 9h30 à 19h30</p>
          </div>
          <div>
            <a
              href="/"
              onClick={(event) => {
                event.preventDefault();
                navigateTo("/");
              }}
            >
              {/* Remplacez 'logo.svg' par le chemin correct de votre logo */}
              <img
                src={process.env.PUBLIC_URL + "/logo2.webp"}
                alt="Logo FLIP MY CAR"
                className="LogoFooter"
              />
            </a>
          </div>
        </div>
        <br />
        <div className="FooterSocial">
          {/* Remplacez 'instagram.svg' par le chemin correct de votre logo Instagram */}
          <a href="https://www.instagram.com/flipmycar.fr" target="_blank">
            <img
              src={process.env.PUBLIC_URL + "/instagram.svg"}
              alt="Instagram"
            />{" "}
          </a>
        </div>
        <div className="FooterText">
          <p>
            © 2026 Flipmycar.fr - Tous droits réservés - RCS de Versailles 982 969
            198
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
